import { searchAll, type SearchGroup, type VideoSummary } from "./api.ts";

/** Minimum query length — shorter terms match almost everything. */
export const MIN_QUERY = 2;
/** Providers per federated request; keeps each Worker call under its limits. */
const BATCH_SIZE = 24;
/** How many batches run at once. */
const BATCH_CONCURRENCY = 5;
/**
 * The first request stays small so the page shows results while the rest of
 * the catalogue is still being asked; 143 providers at 8s per batch used to
 * mean 24 seconds of empty screen.
 */
const FIRST_BATCH_SIZE = 8;

/** Completed searches kept in sessionStorage, newest first. */
const MAX_STORED_RUNS = 6;
/** Poster records kept per stored run; a stored run only needs to repaint. */
const MAX_STORED_ITEMS = 400;
const STORAGE_KEY = "aiox.search.runs.v1";
const ACTIVE_KEY = "aiox.search.active.v1";
const LAYOUT_KEY = "aiox.search.layout.v1";

export type SearchState = "running" | "done" | "stopped";

export interface SearchFailure {
  provider: string;
  name: string;
  reason: string;
}

/**
 * One search across a fixed set of provider ids. The object is the source of
 * truth for every surface that shows the search: the results page subscribes
 * to it, and leaving the page does not cancel it, so coming back from a video
 * can paint the same answer instead of asking the catalogue again.
 */
export interface SearchRun {
  /** `query|sorted provider ids` — the cache identity. */
  key: string;
  query: string;
  /** Provider ids resolved for this scope, in display order. */
  ids: string[];
  /** Sites the reader narrowed to; empty means "every site in the category scope". */
  sites: string[];
  groups: SearchGroup[];
  failures: SearchFailure[];
  /** Provider ids that produced a group or a failure. */
  answered: string[];
  /** Results counted across answered groups. */
  total: number;
  batchesTotal: number;
  batchesDone: number;
  state: SearchState;
  startedAt: number;
  finishedAt: number;
  /** Last known scroll offset of the results page. */
  scrollY: number;
  /** Provider ids whose group is shown in full. */
  expanded: string[];
}

export type SearchEvent = "start" | "update" | "done";
type Listener = (run: SearchRun, event: SearchEvent) => void;

interface RunRecord {
  run: SearchRun;
  names: Map<string, string>;
  listeners: Set<Listener>;
  stopped: boolean;
}

const records = new Map<string, RunRecord>();
let hydrated = false;
let activeKey: string | null = null;
let activeLoaded = false;

function loadActive(): void {
  if (activeLoaded) return;
  activeLoaded = true;
  try {
    activeKey = sessionStorage.getItem(ACTIVE_KEY);
  } catch {
    activeKey = null;
  }
}

/**
 * The search the reader was last looking at. It is deliberately not "the most
 * recently finished": reopening Search after watching a title should return to
 * the results that title came from, even if an older run finished later.
 */
export function markSearchActive(run: SearchRun): void {
  activeKey = run.key;
  activeLoaded = true;
  try {
    sessionStorage.setItem(ACTIVE_KEY, run.key);
  } catch {
    /* memory still points at it */
  }
}

export function forgetActiveSearch(): void {
  activeKey = null;
  activeLoaded = true;
  try {
    sessionStorage.removeItem(ACTIVE_KEY);
  } catch {
    /* nothing to clear */
  }
}

export function searchKey(query: string, providerIds: string[]): string {
  return `${query.trim().toLowerCase()}|${[...providerIds].sort().join(",")}`;
}

function batchesOf(ids: string[]): string[][] {
  if (ids.length === 0) return [];
  const out = [ids.slice(0, FIRST_BATCH_SIZE)];
  for (let index = FIRST_BATCH_SIZE; index < ids.length; index += BATCH_SIZE) {
    out.push(ids.slice(index, index + BATCH_SIZE));
  }
  return out.filter((batch) => batch.length > 0);
}

function notify(record: RunRecord, event: SearchEvent): void {
  // A listener may unsubscribe while the loop runs; iterate a copy.
  for (const listener of [...record.listeners]) {
    try {
      listener(record.run, event);
    } catch {
      /* one surface failing must not stop the other listeners */
    }
  }
}

function storedGroups(groups: SearchGroup[]): SearchGroup[] {
  let budget = MAX_STORED_ITEMS;
  return groups.map((group) => {
    if (budget <= 0) return { ...group, items: [] };
    // Keep the card fields only; the detail payload and headers are large and
    // a restored poster needs none of them.
    const items = group.items.slice(0, budget).map(
      ({ id, title, provider, thumbnail, image, duration, quality, date, url }): VideoSummary => ({
        id,
        title,
        provider,
        thumbnail,
        image,
        duration,
        quality,
        date,
        url,
      }),
    );
    budget -= items.length;
    return { ...group, items };
  });
}

function persist(): void {
  try {
    loadActive();
    const stored = [...records.values()]
      .map((record) => record.run)
      .filter((run) => run.state !== "running")
      .sort((left, right) => (right.finishedAt || right.startedAt) - (left.finishedAt || left.startedAt))
      .slice(0, MAX_STORED_RUNS);
    // A reader can revisit an older cached search; that view is the one a
    // reload must restore, even when newer runs would otherwise push it out.
    const active = activeKey ? records.get(activeKey)?.run : undefined;
    if (active && active.state !== "running" && !stored.some((run) => run.key === active.key)) {
      stored.pop();
      stored.push(active);
    }
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(stored.map((run) => ({ ...run, groups: storedGroups(run.groups) }))),
    );
  } catch {
    /* sessionStorage can be unavailable or full; memory still holds the run */
  }
}

function hydrate(): void {
  if (hydrated) return;
  hydrated = true;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const stored = JSON.parse(raw) as SearchRun[];
    if (!Array.isArray(stored)) return;
    for (const run of stored) {
      if (!run || typeof run.key !== "string" || typeof run.query !== "string") continue;
      // A page reload killed anything that was mid-flight.
      if (run.state === "running") {
        run.state = "stopped";
        run.finishedAt = run.startedAt;
      }
      records.set(run.key, {
        run,
        names: new Map(run.groups.map((group) => [group.provider, group.name])),
        listeners: new Set(),
        stopped: true,
      });
    }
  } catch {
    /* a corrupt cache is simply no cache */
  }
}

function prune(): void {
  loadActive();
  if (records.size <= MAX_STORED_RUNS * 2) return;
  const finished = [...records.entries()]
    .filter(([key, record]) => key !== activeKey && record.run.state !== "running")
    .sort((left, right) => left[1].run.finishedAt - right[1].run.finishedAt);
  for (const [key] of finished.slice(0, records.size - MAX_STORED_RUNS)) {
    records.delete(key);
  }
}

async function execute(record: RunRecord, batches: string[][]): Promise<void> {
  const run = record.run;
  run.batchesTotal = batches.length;
  run.batchesDone = 0;
  notify(record, "start");

  let cursor = 0;
  const worker = async (): Promise<void> => {
    while (cursor < batches.length && !record.stopped) {
      const index = cursor++;
      const batch = batches[index];
      let groups: SearchGroup[] = [];
      let reason: string | null = null;
      try {
        groups = await searchAll(run.query, batch, index === 0 ? 3000 : undefined);
      } catch (error) {
        reason = error instanceof Error ? error.message : "Request failed";
      }
      // A stop stops new batches, not the ones already on the wire: those
      // answers are real and the reader keeps them.
      if (reason) {
        for (const id of batch) {
          run.failures.push({
            provider: id,
            name: record.names.get(id) ?? id,
            reason: reason.replace(/^Unable to create new browser:?\s*/i, "browser: "),
          });
          if (!run.answered.includes(id)) run.answered.push(id);
        }
      } else {
        const returned = new Map(groups.map((group) => [group.provider, group]));
        for (const id of batch) {
          const group = returned.get(id);
          if (!group) {
            run.failures.push({
              provider: id,
              name: record.names.get(id) ?? id,
              reason: "No answer",
            });
            if (!run.answered.includes(id)) run.answered.push(id);
            continue;
          }
          if (group.error) {
            run.failures.push({
              provider: id,
              name: group.name || record.names.get(id) || id,
              reason: group.error.replace(/^Unable to create new browser:?\s*/i, "browser: "),
            });
          }
          if (group.items.length > 0) {
            // A group can carry items and a warning; keep both.
            run.groups.push(group);
            run.total += group.items.length;
          }
          if (!run.answered.includes(id)) run.answered.push(id);
        }
      }
      run.batchesDone += 1;
      notify(record, "update");
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(BATCH_CONCURRENCY, batches.length) }, worker),
  );

  if (record.stopped) {
    // Stop already notified and persisted what was on screen; in-flight
    // answers have landed since, so save the fuller partial answer.
    persist();
    return;
  }
  run.state = "done";
  run.finishedAt = Date.now();
  notify(record, "done");
  persist();
  prune();
}

export interface BeginSearchInput {
  query: string;
  /** Resolved provider ids for the current scope. */
  ids: string[];
  /** Sites the reader explicitly picked, kept for restoring the scope. */
  sites?: string[];
  names: Map<string, string>;
  /** Start again from this run, keeping its answered groups. */
  seed?: SearchRun;
  /** Restrict a seeded run to these provider ids (retry the failures). */
  retry?: string[];
}

/**
 * Start (or resume) a fan-out search. The returned run is updated in place;
 * callers should subscribe before painting so no batch is missed.
 */
export function beginSearch(input: BeginSearchInput): SearchRun {
  hydrate();
  const query = input.query.trim();
  const ids = [...new Set(input.ids)];
  const key = searchKey(query, ids);
  const seed = input.seed;
  const retry = input.retry ? new Set(input.retry) : null;

  let groups = seed ? [...seed.groups] : [];
  let failures = seed ? [...seed.failures] : [];
  let answered = seed ? new Set(seed.answered) : new Set<string>();

  if (retry) {
    groups = groups.filter((group) => !retry.has(group.provider));
    failures = failures.filter((failure) => !retry.has(failure.provider));
    for (const id of retry) answered.delete(id);
    // A retried group is re-counted from zero rather than double-counted.
  }

  const pending = ids.filter((id) => (retry ? retry.has(id) : !answered.has(id)));
  const total = groups.reduce((sum, group) => sum + group.items.length, 0);
  const batches = batchesOf(pending);

  const run: SearchRun = {
    key,
    query,
    ids,
    sites: [...(input.sites ?? seed?.sites ?? [])],
    groups,
    failures,
    answered: [...answered],
    total,
    batchesTotal: batches.length,
    batchesDone: 0,
    state: batches.length > 0 ? "running" : "done",
    startedAt: Date.now(),
    finishedAt: 0,
    scrollY: seed?.scrollY ?? 0,
    expanded: [...(seed?.expanded ?? [])],
  };

  const names = new Map(input.names);
  if (seed) {
    for (const group of seed.groups) names.set(group.provider, group.name);
  }

  const record: RunRecord = { run, names, listeners: new Set(), stopped: false };
  records.set(key, record);

  if (batches.length > 0) {
    void execute(record, batches);
  } else {
    run.finishedAt = Date.now();
    notify(record, "done");
    persist();
  }
  return run;
}

/** The in-memory run for this query and scope, if one already exists. */
export function findSearch(query: string, ids: string[]): SearchRun | undefined {
  hydrate();
  return records.get(searchKey(query, ids))?.run;
}

/**
 * The search the reader was last looking at, used to reopen the page on what
 * it showed. It is deliberately not "the most recently finished": reopening
 * Search after watching a title should return to the results that title came
 * from. Clearing the field forgets it.
 */
export function lastSearch(): SearchRun | undefined {
  hydrate();
  loadActive();
  if (!activeKey) return undefined;
  return records.get(activeKey)?.run;
}

export function subscribe(run: SearchRun, listener: Listener): () => void {
  const record = records.get(run.key);
  if (!record || record.run !== run) return () => {};
  record.listeners.add(listener);
  return () => {
    record.listeners.delete(listener);
  };
}

export function stopSearch(run: SearchRun): void {
  const record = records.get(run.key);
  if (!record || record.run !== run || run.state !== "running") return;
  record.stopped = true;
  run.state = "stopped";
  run.finishedAt = Date.now();
  notify(record, "done");
  persist();
}

/** Re-ask the same scope and query, replacing the cached answer. */
export function refreshSearch(run: SearchRun): SearchRun {
  const record = records.get(run.key);
  const names = new Map(record?.names ?? run.groups.map((group) => [group.provider, group.name]));
  for (const failure of run.failures) names.set(failure.provider, failure.name);
  return beginSearch({
    query: run.query,
    ids: run.ids,
    sites: run.sites,
    names,
  });
}

/** Ask only the providers that failed, keeping everything that answered. */
export function retryFailures(run: SearchRun): SearchRun | null {
  if (run.failures.length === 0) return null;
  const record = records.get(run.key);
  const names = new Map(record?.names ?? []);
  for (const failure of run.failures) names.set(failure.provider, failure.name);
  return beginSearch({
    query: run.query,
    ids: run.ids,
    sites: run.sites,
    names,
    seed: run,
    retry: run.failures.map((failure) => failure.provider),
  });
}

export function setRunScroll(run: SearchRun, offset: number): void {
  if (run.scrollY === offset) return;
  run.scrollY = Math.max(0, Math.round(offset));
}

export function setRunExpanded(run: SearchRun, provider: string, expanded: boolean): void {
  const next = new Set(run.expanded);
  if (expanded) next.add(provider);
  else next.delete(provider);
  run.expanded = [...next];
}

export type SearchLayout = "grouped" | "flat";

export function readLayout(): SearchLayout {
  try {
    const stored = localStorage.getItem(LAYOUT_KEY);
    return stored === "flat" ? "flat" : "grouped";
  } catch {
    return "grouped";
  }
}

export function writeLayout(layout: SearchLayout): void {
  try {
    localStorage.setItem(LAYOUT_KEY, layout);
  } catch {
    /* a preference that cannot be stored still works for this page */
  }
}

// ---- single-provider search memory ----

export interface ProviderSearchEntry {
  provider: string;
  query: string;
  items: VideoSummary[];
  at: number;
  scrollY: number;
}

const providerSearches = new Map<string, ProviderSearchEntry>();
const providerLast = new Map<string, string>();

export function rememberProviderSearch(
  provider: string,
  query: string,
  items: VideoSummary[],
): ProviderSearchEntry {
  const prior = providerSearches.get(provider);
  const entry: ProviderSearchEntry = {
    provider,
    query,
    items,
    at: Date.now(),
    scrollY: prior && prior.query.toLowerCase() === query.toLowerCase() ? prior.scrollY : 0,
  };
  providerSearches.set(provider, entry);
  providerLast.set(provider, query);
  if (providerSearches.size > 20) {
    const oldest = [...providerSearches.entries()].sort((left, right) => left[1].at - right[1].at)[0];
    if (oldest) providerSearches.delete(oldest[0]);
  }
  return entry;
}

export function lastProviderSearch(provider: string): ProviderSearchEntry | undefined {
  const query = providerLast.get(provider);
  if (!query) return undefined;
  const entry = providerSearches.get(provider);
  return entry && entry.query === query ? entry : undefined;
}

export function setProviderSearchScroll(provider: string, offset: number): void {
  const entry = providerSearches.get(provider);
  if (entry) entry.scrollY = Math.max(0, Math.round(offset));
}
